import jsPDF from 'jspdf';

export const PDF_TYPOGRAPHY = {
  fontSizes: {
    hero: 32,
    h1: 24,
    h2: 18,
    h3: 14,
    body: 12,
    small: 10,
    priceValue: 16,
    pageTitle: 24,
    sectionHeader: 18,
    serviceName: 14,
    price: 16,
    description: 11,
    featureText: 11,
    clientInfo: 12,
    clientInfoLabel: 10,
    totalDueNow: 20,
    totalDueNowAmount: 32,
    summaryItem: 12,
    summaryPrice: 16,
    savingsLabel: 12,
    savingsItem: 10,
    thenMonthly: 10,
    strikethroughPrice: 14,
  },

  lineHeights: {
    hero: 1.2,
    headers: 1.3,
    body: 1.5,
    list: 6,
    description: 5.5,
    feature: 5,
    normal: 7,
    tight: 6,
    loose: 8,
  },

  spacing: {
    majorSection: 20,
    servicePackage: 16,
    subsection: 16,
    lineItem: 8,
    bullet: 6,
    sectionGap: 12,
    itemGap: 6,
    subItemGap: 4,
    beforeSection: 8,
    afterSection: 6,
    listIndent: 20,
    featureIndent: 8,
  },

  colors: {
    darkTeal: { r: 30, g: 90, b: 110 },
    textPrimary: { r: 0, g: 0, b: 0 },
    textSecondary: { r: 51, g: 51, b: 51 },
    textMuted: { r: 102, g: 102, b: 102 },
    textLight: { r: 153, g: 153, b: 153 },
    green: { r: 22, g: 163, b: 74 },
    accentColor: { r: 212, g: 245, b: 110 },
    lightGray: { r: 248, g: 249, b: 250 },
    border: { r: 229, g: 231, b: 235 },
    strikethrough: { r: 153, g: 153, b: 153 },
  },

  margins: {
    page: 14,
    contentLeft: 22,
    contentRight: 185,
    indent: 27,
    doubleIndent: 35,
  },

  maxWidths: {
    description: 158,
    features: 153,
    clientInfo: 85,
  },
};

export interface BrandTheme {
  name: string;
  primaryColor: string;
  secondaryColor: string;
  fontFamily?: string;
  headerBackground?: string;
  bodyTextColor?: string;
  mutedTextColor?: string;
}

export function getBrandTheme(brandName: string, primaryColor: string, secondaryColor: string): BrandTheme {
  const theme: BrandTheme = {
    name: brandName,
    primaryColor,
    secondaryColor,
  };

  if (brandName.toLowerCase().includes('web design miami')) {
    theme.fontFamily = 'helvetica';
    theme.headerBackground = '#FFFFFF';
    theme.bodyTextColor = '#333333';
    theme.mutedTextColor = '#666666';
  }

  return theme;
}

export interface RenderOptions {
  maxWidth?: number;
  fontSize?: number;
  lineHeight?: number;
  indent?: number;
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
}

export function checkPageBreak(doc: jsPDF, currentY: number, neededSpace: number = 20): number {
  if (currentY > 270 - neededSpace) {
    doc.addPage();
    return 20;
  }
  return currentY;
}

export function setColor(doc: jsPDF, color: { r: number; g: number; b: number }): void {
  doc.setTextColor(color.r, color.g, color.b);
}

export function renderPageTitle(doc: jsPDF, title: string, y: number, primaryColor: { r: number; g: number; b: number }): number {
  doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h1);
  doc.setFont('helvetica', 'bold');
  setColor(doc, PDF_TYPOGRAPHY.colors.darkTeal);
  doc.text(title, PDF_TYPOGRAPHY.margins.page, y);

  doc.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
  doc.setLineWidth(1.5);
  doc.line(PDF_TYPOGRAPHY.margins.page, y + 2, 100, y + 2);

  return y + PDF_TYPOGRAPHY.spacing.subsection;
}

export function renderSectionHeader(doc: jsPDF, title: string, y: number, primaryColor: { r: number; g: number; b: number }): number {
  const lightGray = PDF_TYPOGRAPHY.colors.lightGray;
  const darkTeal = PDF_TYPOGRAPHY.colors.darkTeal;

  doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
  doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, 180, 10, 1.5, 1.5, 'F');

  doc.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b);
  doc.rect(PDF_TYPOGRAPHY.margins.page, y, 4, 10, 'F');

  doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h2);
  doc.setFont('helvetica', 'bold');
  setColor(doc, darkTeal);
  doc.text(title, PDF_TYPOGRAPHY.margins.contentLeft, y + 7);

  setColor(doc, PDF_TYPOGRAPHY.colors.textPrimary);
  doc.setFont('helvetica', 'normal');

  return y + PDF_TYPOGRAPHY.spacing.subsection;
}

export function renderServiceItem(
  doc: jsPDF,
  name: string,
  price: string,
  y: number,
  description?: string | null,
  features?: any,
  renderFeaturesFunc?: (features: any, startY: number, maxWidth?: number) => number
): number {
  let currentY = y;

  doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
  doc.setFont('helvetica', 'bold');
  setColor(doc, PDF_TYPOGRAPHY.colors.textPrimary);
  doc.text(name, PDF_TYPOGRAPHY.margins.contentLeft, currentY);

  doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.priceValue);
  doc.text(price, PDF_TYPOGRAPHY.margins.contentRight, currentY, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  currentY += PDF_TYPOGRAPHY.spacing.itemGap;

  if (description) {
    doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.description);
    setColor(doc, PDF_TYPOGRAPHY.colors.textMuted);
    const descLines = doc.splitTextToSize(description, PDF_TYPOGRAPHY.maxWidths.description);
    doc.text(descLines, PDF_TYPOGRAPHY.margins.indent, currentY);
    currentY += descLines.length * PDF_TYPOGRAPHY.lineHeights.description + 2;
    setColor(doc, PDF_TYPOGRAPHY.colors.textPrimary);
  }

  if (features && renderFeaturesFunc) {
    currentY = renderFeaturesFunc(features, currentY, PDF_TYPOGRAPHY.maxWidths.features);
    currentY += 2;
  }

  return currentY;
}

export function renderAdditionalItem(
  doc: jsPDF,
  label: string,
  price: string,
  y: number,
  description?: string | null
): number {
  let currentY = y;

  doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.body);
  setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
  doc.setFont('helvetica', 'normal');
  doc.text(label, PDF_TYPOGRAPHY.margins.indent, currentY);

  doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
  doc.setFont('helvetica', 'bold');
  doc.text(price, PDF_TYPOGRAPHY.margins.contentRight, currentY, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  currentY += PDF_TYPOGRAPHY.lineHeights.normal + 1;

  if (description) {
    doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.description);
    setColor(doc, PDF_TYPOGRAPHY.colors.textMuted);
    const split = doc.splitTextToSize(description, PDF_TYPOGRAPHY.maxWidths.description);
    doc.text(split, PDF_TYPOGRAPHY.margins.indent, currentY);
    currentY += split.length * PDF_TYPOGRAPHY.lineHeights.description + 2;
    setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
  }

  return currentY;
}

export function renderFeaturesToPDF(
  doc: jsPDF,
  features: any,
  startY: number,
  maxWidth: number = PDF_TYPOGRAPHY.maxWidths.features
): number {
  let y = startY;

  if (!features) return y;

  if (features.sections && Array.isArray(features.sections)) {
    features.sections.forEach((section: any) => {
      y = checkPageBreak(doc, y);

      if (section.title) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.body);
        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
        doc.text(section.title, PDF_TYPOGRAPHY.margins.indent, y);
        y += PDF_TYPOGRAPHY.lineHeights.tight + 1;
      }

      if (section.description) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.description);
        setColor(doc, PDF_TYPOGRAPHY.colors.textMuted);
        const descLines = doc.splitTextToSize(section.description, maxWidth);
        doc.text(descLines, PDF_TYPOGRAPHY.margins.indent, y);
        y += descLines.length * PDF_TYPOGRAPHY.lineHeights.feature + 1;
      }

      if (section.items && Array.isArray(section.items)) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.description);
        setColor(doc, PDF_TYPOGRAPHY.colors.textMuted);
        section.items.forEach((item: string) => {
          y = checkPageBreak(doc, y);
          const itemLines = doc.splitTextToSize(`• ${item}`, maxWidth - PDF_TYPOGRAPHY.spacing.featureIndent);
          doc.text(itemLines, PDF_TYPOGRAPHY.margins.doubleIndent, y);
          y += itemLines.length * PDF_TYPOGRAPHY.lineHeights.list + 1;
        });
      }
      y += PDF_TYPOGRAPHY.spacing.subItemGap;
    });
  } else if (Array.isArray(features)) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.description);
    setColor(doc, PDF_TYPOGRAPHY.colors.textMuted);
    features.forEach((feature: any) => {
      y = checkPageBreak(doc, y);
      if (typeof feature === 'string') {
        const featureLines = doc.splitTextToSize(`• ${feature}`, maxWidth);
        doc.text(featureLines, PDF_TYPOGRAPHY.margins.indent, y);
        y += featureLines.length * PDF_TYPOGRAPHY.lineHeights.list + 1;
      }
    });
  }

  return y;
}
